import React, { useState } from 'react';
import {
  Box,
  Button,
  Heading,
  Text,
  VStack,
  HStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Flex,
  Tooltip,
} from '@chakra-ui/react';
import { FiPlus, FiEdit, FiTrash2, FiMapPin, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { useUserContext } from '../../context/UserContext';
import {
  useGetBranches,
  useCreateBranch,
  useUpdateBranch,
  useDeleteBranch,
  useActivateBranch,
  useDeactivateBranch,
} from '../../services/companyService';

const BranchManagement = () => {
  const { isAdmin, canManageBranches } = useUserContext();
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    contact_email: '',
    description: '',
  });
  const [isEditing, setIsEditing] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  const { branches, branchesIsLoading, branchesError, refetchBranches } = useGetBranches();
  const { createBranchPayload, createBranchIsLoading } = useCreateBranch(() => {
    onClose();
    refetchBranches();
    resetForm();
  });

  const { updateBranchPayload, updateBranchIsLoading } = useUpdateBranch(() => {
    onClose();
    refetchBranches();
    resetForm();
  });

  const { deleteBranchPayload, deleteBranchIsLoading } = useDeleteBranch(() => {
    onDeleteClose();
    refetchBranches();
  });

  const { activateBranchPayload, activateBranchIsLoading } = useActivateBranch(() => {
    refetchBranches();
  });

  const { deactivateBranchPayload, deactivateBranchIsLoading } = useDeactivateBranch(() => {
    refetchBranches();
  });

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      phone: '',
      email: '',
      contact_email: '',
      description: '',
    });
    setSelectedBranch(null);
    setIsEditing(false);
  };

  const handleOpenModal = (branch = null) => {
    if (branch) {
      setSelectedBranch(branch);
      setFormData({
        name: branch.name,
        address: branch.address || '',
        phone: branch.phone || '',
        email: branch.email || '',
        contact_email: branch.contact_email || '',
        description: branch.description || '',
      });
      setIsEditing(true);
    } else {
      resetForm();
    }
    onOpen();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.contact_email) {
      // You can add toast notification here if needed
      return;
    }
    
    if (isEditing && selectedBranch) {
      updateBranchPayload({
        branchId: selectedBranch.branch_id,
        payload: formData,
      });
    } else {
      createBranchPayload(formData);
    }
  };

  const handleDelete = () => {
    if (selectedBranch) {
      deleteBranchPayload(selectedBranch.branch_id);
    }
  };

  const handleActivateBranch = (branchId) => {
    activateBranchPayload(branchId);
  };

  const handleDeactivateBranch = (branchId) => {
    deactivateBranchPayload(branchId);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Check permissions
  if (!isAdmin || !canManageBranches) {
    return (
      <Box p={6}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription>
              You don't have permission to access branch management.
            </AlertDescription>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        <Flex justify="space-between" align="center">
          <Box>
            <Heading size="lg" color="gray.900" mb={2}>
              Branch Management
            </Heading>
            <Text color="gray.600">
              Manage your company branches and locations
            </Text>
          </Box>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            onClick={() => handleOpenModal()}
          >
            Add Branch
          </Button>
        </Flex>

        {branchesIsLoading ? (
          <Flex justify="center" py={8}>
            <Spinner size="lg" color="blue.500" />
          </Flex>
        ) : branchesError ? (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Failed to Load Branches</AlertTitle>
              <AlertDescription>
                {branchesError}
              </AlertDescription>
            </Box>
          </Alert>
        ) : (
          <Box
            bg="white"
            borderRadius="lg"
            shadow="sm"
            border="1px solid"
            borderColor="gray.200"
            overflow="hidden"
          >
            <Table variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Branch Name</Th>
                  <Th>Address</Th>
                  <Th>Contact</Th>
                  <Th>Employees</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {branches && branches.length > 0 ? (
                  branches.map((branch) => (
                    <Tr key={branch.branch_id}>
                      <Td>
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="600">{branch.name}</Text>
                          {branch.description && (
                            <Text fontSize="sm" color="gray.500">
                              {branch.description}
                            </Text>
                          )}
                        </VStack>
                      </Td>
                      <Td>
                        <HStack spacing={1}>
                          <FiMapPin size={14} color="#6B7280" />
                          <Text fontSize="sm">{branch.address || 'N/A'}</Text>
                        </HStack>
                      </Td>
                      <Td>
                        <VStack align="start" spacing={1}>
                          {branch.phone && (
                            <Text fontSize="sm">{branch.phone}</Text>
                          )}
                          {branch.contact_email && (
                            <Text fontSize="sm" color="blue.500">
                              {branch.contact_email}
                            </Text>
                          )}
                        </VStack>
                      </Td>
                      <Td>
                        <Badge colorScheme="blue" variant="subtle">
                          {branch.total_employees || 0} employees
                        </Badge>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={branch.status === 'active' ? 'green' : 'gray'}
                          variant="subtle"
                        >
                          {branch.status === 'active' ? 'Active' : 'Inactive'}
                        </Badge>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <Tooltip label="Edit branch" placement="top">
                            <IconButton
                              icon={<FiEdit />}
                              size="sm"
                              variant="ghost"
                              colorScheme="blue"
                              onClick={() => handleOpenModal(branch)}
                              aria-label="Edit branch"
                            />
                          </Tooltip>
                          {branch.status === 'active' ? (
                            <Tooltip label="Deactivate branch" placement="top">
                              <IconButton
                                icon={<FiXCircle />}
                                size="sm"
                                variant="ghost"
                                colorScheme="orange"
                                onClick={() => handleDeactivateBranch(branch.branch_id)}
                                aria-label="Deactivate branch"
                                isLoading={deactivateBranchIsLoading}
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip label="Activate branch" placement="top">
                              <IconButton
                                icon={<FiCheckCircle />}
                                size="sm"
                                variant="ghost"
                                colorScheme="green"
                                onClick={() => handleActivateBranch(branch.branch_id)}
                                aria-label="Activate branch"
                                isLoading={activateBranchIsLoading}
                              />
                            </Tooltip>
                          )}
                          <Tooltip 
                            label={branch.total_employees > 0 ? "Cannot delete branch with employees" : "Delete branch"} 
                            placement="top"
                          >
                            <IconButton
                              icon={<FiTrash2 />}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => {
                                setSelectedBranch(branch);
                                onDeleteOpen();
                              }}
                              aria-label="Delete branch"
                              isDisabled={branch.total_employees > 0}
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan={6} textAlign="center" py={8}>
                      <VStack spacing={2}>
                        <Text color="gray.500" fontSize="sm">
                          No branches found
                        </Text>
                        <Text color="gray.400" fontSize="xs">
                          Click "Add Branch" to create your first branch
                        </Text>
                      </VStack>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        )}
      </VStack>

      {/* Add/Edit Branch Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={handleSubmit}>
            <ModalHeader>
              {isEditing ? 'Edit Branch' : 'Add New Branch'}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Branch Name</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter branch name"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Address</FormLabel>
                  <Textarea
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="Enter branch address"
                    rows={3}
                  />
                </FormControl>

                <HStack spacing={4} w="full">
                  <FormControl>
                    <FormLabel>Phone</FormLabel>
                    <Input
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="Enter phone number"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Email</FormLabel>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="Enter email address"
                    />
                  </FormControl>
                </HStack>

                <FormControl isRequired>
                  <FormLabel>Contact Email</FormLabel>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => handleInputChange('contact_email', e.target.value)}
                    placeholder="Enter contact email (required)"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Enter branch description"
                    rows={3}
                  />
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={createBranchIsLoading || updateBranchIsLoading}
              >
                {isEditing ? 'Update Branch' : 'Create Branch'}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Branch</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to delete the branch "{selectedBranch?.name}"?
              This action cannot be undone.
            </Text>
            {selectedBranch?.employee_count > 0 && (
              <Alert status="warning" mt={4}>
                <AlertIcon />
                <Box>
                  <AlertTitle>Cannot Delete</AlertTitle>
                  <AlertDescription>
                    This branch has {selectedBranch.total_employees} employees and cannot be deleted.
                  </AlertDescription>
                </Box>
              </Alert>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleDelete}
              isLoading={deleteBranchIsLoading}
              isDisabled={selectedBranch?.employee_count > 0}
            >
              Delete Branch
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default BranchManagement; 